import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@components/ui/dialog';
import { Button } from '@components/ui/button';
import { useAvailability } from '@hooks/usePublicBooking';
import { useReschedulePublicBooking } from '@hooks/usePublicBooking';
import { PageLoading } from '@components/common/LoadingSpinner';
import { ErrorMessage } from '@components/common/ErrorMessage';
import { formatTime } from '@lib/utils';
import type { TimeSlot } from '@api/types';
import { cn } from '@lib/utils';
import { useToast } from '@hooks/useToast';
import { nextDaysInTimezone } from '@lib/timezone';

interface CustomerRescheduleProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trackingToken: string;
  centerSlug: string;
  /** IANA timezone of the centre — slot times + weekday labels rendered in this zone. */
  centerTimezone: string;
  serviceId: string;
  staffId?: string;
}

export function CustomerReschedule({
  open,
  onOpenChange,
  trackingToken,
  centerSlug,
  centerTimezone,
  serviceId,
  staffId,
}: CustomerRescheduleProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  // M-8 closeout: igual que widget DateTimeStep, antes este `getNextDays`
  // local usaba UTC del navegador. Reschedule además parte de "mañana"
  // (no hoy), así que pedimos 15 días y descartamos el primero.
  const days = nextDaysInTimezone(15, centerTimezone).slice(1);
  const [selectedDate, setSelectedDate] = useState(days[0] ?? '');
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);

  const params: Record<string, string> = { serviceId, date: selectedDate };
  if (staffId) params.staffId = staffId;

  const { data: slots, isLoading, isError, refetch } = useAvailability(centerSlug, params);
  const rescheduleMutation = useReschedulePublicBooking(trackingToken);

  const availableSlots = slots?.filter((s) => s.available) ?? [];

  const handleConfirm = async () => {
    if (!selectedSlot) return;
    try {
      await rescheduleMutation.mutateAsync({
        newStartTime: selectedSlot.startTime,
        staffId,
      });
      toast({ title: t('booking.actions.reschedule') + ' OK' });
      onOpenChange(false);
    } catch {
      toast({ title: t('errors.serverError'), variant: 'destructive' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="reschedule-dialog">
        <DialogHeader>
          <DialogTitle>{t('booking.actions.reschedule')}</DialogTitle>
        </DialogHeader>

        {/* Date strip */}
        <div
          className="flex gap-2 overflow-x-auto pb-2"
          role="group"
          aria-label="Selecciona fecha de reprogramación"
        >
          {days.map((day) => {
            const d = new Date(day + 'T00:00:00');
            return (
              <button
                key={day}
                type="button"
                onClick={() => { setSelectedDate(day); setSelectedSlot(null); }}
                className={cn(
                  'flex shrink-0 flex-col items-center rounded-md border px-2.5 py-1.5 text-xs transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  selectedDate === day
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'hover:border-primary',
                )}
                aria-pressed={selectedDate === day}
                data-testid={`reschedule-date-${day}`}
              >
                <span className="font-medium">{d.getDate()}</span>
                <span className="opacity-80">{d.toLocaleDateString('es-ES', { weekday: 'short', timeZone: centerTimezone })}</span>
              </button>
            );
          })}
        </div>

        {/* Slots */}
        {isLoading && <PageLoading />}
        {isError && <ErrorMessage onRetry={() => void refetch()} />}

        {!isLoading && !isError && availableSlots.length === 0 && (
          <p className="text-sm text-muted-foreground">{t('widget.noAvailability')}</p>
        )}

        {!isLoading && availableSlots.length > 0 && (
          <ul className="grid grid-cols-4 gap-2" role="list" data-testid="reschedule-slots">
            {availableSlots.map((slot) => (
              <li key={slot.startTime}>
                <button
                  type="button"
                  onClick={() => { setSelectedSlot(slot); }}
                  className={cn(
                    'w-full rounded-md border py-1.5 text-xs transition-colors',
                    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    selectedSlot?.startTime === slot.startTime
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'hover:border-primary',
                  )}
                  aria-pressed={selectedSlot?.startTime === slot.startTime}
                  data-testid={`reschedule-slot-${slot.startTime}`}
                >
                  {formatTime(slot.startTime, { timezone: centerTimezone })}
                </button>
              </li>
            ))}
          </ul>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => { onOpenChange(false); }}>
            {t('common.cancel')}
          </Button>
          <Button
            disabled={!selectedSlot || rescheduleMutation.isPending}
            onClick={() => void handleConfirm()}
            data-testid="reschedule-confirm"
          >
            {rescheduleMutation.isPending ? t('common.loading') : t('common.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
